/**
 * Authentication utilities for token management and auto-logout
 */

const TOKEN_KEY = 'token';
const TOKEN_TIMESTAMP_KEY = 'token_timestamp';
const TOKEN_EXPIRY_HOURS = 24;

/**
 * Store token with timestamp for expiration tracking
 */
export const setAuthToken = (token) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(TOKEN_TIMESTAMP_KEY, Date.now().toString());
};

/**
 * Get auth token
 */
export const getAuthToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Check if token is expired (24 hours)
 */
export const isTokenExpired = () => {
  const timestamp = localStorage.getItem(TOKEN_TIMESTAMP_KEY);
  if (!timestamp) return true;
  
  const tokenAge = Date.now() - parseInt(timestamp);
  const hoursElapsed = tokenAge / (1000 * 60 * 60);
  
  return hoursElapsed >= TOKEN_EXPIRY_HOURS;
};

/**
 * Clear all auth data
 */
export const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_TIMESTAMP_KEY);
  localStorage.removeItem('user');
};

/**
 * Logout and redirect to login
 */
export const logout = () => {
  clearAuth();
  window.location.href = '/';
};

/**
 * Check token validity and auto-logout if expired
 * Call this on app load and periodically
 */
export const checkAuthValidity = () => {
  const token = getAuthToken();
  
  if (!token) {
    return false;
  }
  
  if (isTokenExpired()) {
    console.log('Token expired - logging out');
    logout();
    return false;
  }
  
  return true;
};

/**
 * Setup axios interceptor to handle 401 errors globally
 */
export const setupAuthInterceptor = (axios) => {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      // Handle 401 Unauthorized - token expired or invalid
      // Skip platform admin endpoints — they have their own auth handling
      // Skip auth endpoints themselves (login/signup/accept-invite/password-reset)
      // so that the originating component can render the proper error toast
      // instead of getting force-redirected to the login page.
      const url = error.config?.url || '';
      const isPlatformEndpoint = url.includes('/super-admin/');
      const isAuthEndpoint =
        url.includes('/auth/login') ||
        url.includes('/auth/signup') ||
        url.includes('/auth/accept-invite') ||
        url.includes('/auth/password-reset') ||
        url.includes('/auth/forgot-password') ||
        url.includes('/auth/change-password');
      if (
        error.response &&
        error.response.status === 401 &&
        !isPlatformEndpoint &&
        !isAuthEndpoint
      ) {
        console.log('401 Unauthorized - logging out');
        logout();
      }
      return Promise.reject(error);
    }
  );
};
